import { PrinterKioskAlertType } from './../types/PrinterKioskAlertType';
import { CashKioskAlertType } from './../types/CashKioskAlertType';
import { KioskState } from './../typegen/kiosk-state';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import api from '../api/api';
import KioAppHelper from '../helpers/kioApp';
import logger from '../helpers/logger';
import { KioskAlertType } from '../typegen/kiosk-alert-type';
import { SupportedDevices } from '../typegen/supported-devices';
import passKeyHelper from '../helpers/passKey';
import { store } from './store';
import { LecacyPostCashBoxRemoved } from '../types/legacyAdminApi';
import sqlite from '../helpers/sqlite';
import { ulid } from 'ulid';
import { ApiCallServiceType } from '../types/ApiCall';
import { CheckKioskAlertType } from '../types/CheckKioskAlertType';
import { PaymentMethod } from '../typegen/payment-method';
import { PaymentType } from '../typegen/payment-type';

export type PaymentMethodCheckResult = {
	canPay: boolean;
	limitReason?: 'over-limit' | 'under-limit';
};

export default class kioskStore {
	private _hubReconnectDelay = 300000;
	hubConnection: HubConnection | null = null;
	connectionStatus: 'Connected' | 'Connecting' | 'Offline' = 'Connecting';
	private _kioskId?: number;
	private _isKioskAuthorized = false;
	private _lastKioskStateOnServer?: KioskState;
	kioskName: string = '';
	private _useGlobalBarcodeScanner: boolean = true; // Fixed value for now, can be made dynamic if needed in the future
	private _sentAppAlerts: Map<string, boolean> = new Map();
	private canTakeCashPayments: boolean | null = null;

	constructor() {
		makeAutoObservable(this);

		reaction(
			// Observe lastKioskStateOnServer changes and set outOfService accordingly
			() => this.lastKioskStateOnServer,
			(newState) => {
				switch (newState) {
					case KioskState.Active:
						this.setOutOfService(false);
						break;
					case KioskState.Maintenance:
					case KioskState.Unavailable:
					case KioskState.CashBoxAlarm:
					case KioskState.OutOfService:
						this.setOutOfService(true);
						break;
				}
			}
		);
	}

	get canAcceptCashPayments() {
		return this.canTakeCashPayments;
	}

	setCanTakeCashPayments = async () => {
		logger.debug.log('Setting canTakeCashPayments value in kioskStore');
		if (this.isDeviceSupported(SupportedDevices.CashAcceptor) === false) {
			this.canTakeCashPayments = false;
			return;
		}

		try {
			this.canTakeCashPayments = !(await store.bpkManagerStore.isCashBoxFull());
		} catch (error) {
			logger.logGeneral.error({}, 'Error setting canTakeCashPayments', error);
			this.canTakeCashPayments = false;
		}
	};

	get kioskId() {
		if (this._kioskId) return this._kioskId;
		this._kioskId = 0;

		if (import.meta.env.DEV && import.meta.env.VITE_IGNORE_KW_AUTH == 'true') {
			try {
				const kioskId = parseInt(import.meta.env.VITE_KIOSK_ID, 10);
				this._kioskId = kioskId;
			} catch (error) {
				logger.debug.error('There was a problem parsing the kioskId value from the VITE env variable', JSON.stringify(error));
			}
		} else {
			try {
				const kiowareKeyStringValue = KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_KIOSK_ID);
				const kioskId = parseInt(KioAppHelper('DoUrlSubstitution', kiowareKeyStringValue!)!, 10);
				this._kioskId = kioskId;
			} catch (error) {
				logger.debug.error('There was a problem parsing the KIO_SERIAL_NUMBER value from KioWare', JSON.stringify(error));
			}
		}

		return this._kioskId;
	}

	get useGlobalBarcodeScanner() {
		return this._useGlobalBarcodeScanner;
	}

	get companyId() {
		return store.configStore.companyId;
	}

	get lastKioskStateOnServer(): KioskState | undefined {
		return this._lastKioskStateOnServer;
	}

	setKioskName = (name: string) => {
		this.kioskName = name;
	};

	authenticate = async () => {
		const isAuthorized = await api.kiosks.auth();
		runInAction(() => {
			this._isKioskAuthorized = isAuthorized;
			if (this._isKioskAuthorized) logger.debug.log(`Kiosk with id ${this.kioskId} has been authorized`);
			else logger.debug.error(`Kiosk with id ${this.kioskId} has not been authorized`);
		});
	};

	get isKioskAuthorized() {
		return this._isKioskAuthorized;
	}

	createHubConnection = () => {
		if (!this.isKioskAuthorized) return;
		this.hubConnection = new HubConnectionBuilder()
			.withUrl(import.meta.env.VITE_SIGNALR_KIOSKHUB_URL)
			.withAutomaticReconnect()
			.configureLogging(LogLevel.Information)
			.build();

		this.hubConnection
			.start()
			.then(() => runInAction(() => (this.connectionStatus = 'Connected')))
			.then(() => this.hubConnection?.invoke('JoinKiosks', this.hubConnection.connectionId))
			.then(() => logger.debug.log(`Joined Kiosks`))
			.then(() => this.hubConnection?.invoke('JoinKiosk', this.hubConnection.connectionId, String(this.kioskId)))
			.then(() => logger.debug.log(`Joined Kiosk: ${this.kioskId}`))
			.catch((error) => {
				runInAction(() => {
					logger.debug.error('Error starting hub connection: ', error);
					this.connectionStatus = 'Offline';
					setTimeout(() => this.createHubConnection(), this._hubReconnectDelay);
				});
			});

		this.hubConnection.onreconnecting(() => runInAction(() => (this.connectionStatus = 'Connecting')));
		this.hubConnection.onreconnected(() => runInAction(() => (this.connectionStatus = 'Connected')));
		this.hubConnection.onclose(() =>
			runInAction(() => {
				this.connectionStatus = 'Offline';
				setTimeout(() => this.createHubConnection(), this._hubReconnectDelay);
			})
		);

		this.hubConnection.on('JokeWasJudged', (veredict: string) => {
			runInAction(() => this.handleJokeVeredict(veredict));
		});

		this.hubConnection.on('KioskStateUpdated', (newState: KioskState) => {
			this.handleLastKioskStateUpdated(newState);
		});
	};

	handleLastKioskStateUpdated = (newState: KioskState) => {
		runInAction(() => {
			this._lastKioskStateOnServer = newState;
			logger.logGeneral.log({}, `Kiosk state updated to: ${newState}`);
		});
	};

	setLastKioskStateOnConfigLoad = (kioskState: KioskState) => {
		runInAction(() => {
			this._lastKioskStateOnServer = kioskState;
		});
	};

	updateKioskStateOnServer = async (kioskState: KioskState) => {
		try {
			await this.hubConnection?.invoke('UpdateKioskState', { kioskState, kioskId: this.kioskId });
			runInAction(() => {
				this._lastKioskStateOnServer = kioskState;
			});
			logger.logGeneral.log({}, `Kiosk state updated on server via SignalR to: ${kioskState}`);
		} catch (error) {
			logger.logGeneral.error({}, 'Error updating kiosk state on server via SignalR:', error);
		}
		return;
	};

	stopHubConnection = async () => {
		this.hubConnection?.stop().catch((error) => console.error('Error stopping hub connection:', error));
	};

	handleJokeVeredict = (veredict: string) => {
		alert(veredict);
	};

	submitJokeToJudge = async (joke: string) => {
		try {
			await this.hubConnection?.invoke('TellMeAJoke', { joke });
		} catch (error) {
			console.error(error);
		}
	};

	setOutOfService = (outOfService: boolean) => {
		KioAppHelper('OutOfService', outOfService);
	};

	toggleOutOfService = async () => {
		switch (this.lastKioskStateOnServer) {
			case KioskState.Active:
			case KioskState.Unavailable:
				await this.updateKioskStateOnServer(KioskState.OutOfService);
				this.setOutOfService(true);
				break;
			case KioskState.OutOfService:
				await this.updateKioskStateOnServer(KioskState.Active);
				this.setOutOfService(false);
				break;
			case KioskState.CashBoxAlarm:
			case KioskState.Maintenance:
			default:
				break;
		}
	};

	isDeviceSupported = (device: SupportedDevices): boolean => {
		return (store.configStore.supportedDevices & device) === device;
	};

	/**
	 * Sends a check reader alert if the check reader is supported on the kiosk.
	 * @param {string} customMessage - The legacy BPK service requires this field but once it is removed we can make it optional
	 */
	sendCheckAlert = async (alertType: CheckKioskAlertType, customMessage: string, isServiceAlert?: boolean) => {
		if (this.isDeviceSupported(SupportedDevices.CheckReader)) await this.sendAlert(alertType, customMessage, isServiceAlert);
		else logger.debug.log(`Attempted to send check alert of type ${alertType}, but check reader is not supported on this kiosk.`);
	};

	/**
	 * Sends a cash acceptor alert if the cash acceptor is supported on the kiosk.
	 * @param {string} customMessage - The legacy BPK service requires this field but once it is removed we can make it optional
	 */
	sendCashAlert = async (alertType: CashKioskAlertType, customMessage: string, isServiceAlert?: boolean) => {
		if (this.isDeviceSupported(SupportedDevices.CashAcceptor)) await this.sendAlert(alertType, customMessage, isServiceAlert);
		else logger.debug.log(`Attempted to send cash alert of type ${alertType}, but cash acceptor is not supported on this kiosk.`);
	};

	/**
	 * Sends a printer alert if the printer is supported on the kiosk.
	 * @param {string} customMessage - The legacy BPK service requires this field but once it is removed we can make it optional
	 */
	sendPrinterAlert = async (alertType: PrinterKioskAlertType, customMessage: string, isServiceAlert?: boolean) => {
		if (this.isDeviceSupported(SupportedDevices.ReceiptPrinter)) await this.sendAlert(alertType, customMessage, isServiceAlert);
		else logger.debug.log(`Attempted to send printer alert of type ${alertType}, but printer is not supported on this kiosk.`);
	};

	/**
	 * Sends alerts to the legacy BPK service and logs the entry on the new api DB;
	 * @remarks If using directly to send alert to device, you should check if the device is available
	 * @param {string} customMessage - The legacy BPK service requires this field but once it is removed we can make it optional
	 * @param {boolean} [isServiceAlert] - Indicates if the alert is being sent as part of the service
	 */
	sendAlert = async (alertType: keyof typeof KioskAlertType, customMessage: string, isServiceAlert?: boolean) => {
		if (import.meta.env.VITE_DISABLE_ALERTS === 'true') {
			logger.debug.warn(`Alert of type ${alertType} with message: ${customMessage}. Will not be sent because alerts are disabled.`);
			return;
		}

		if (!isServiceAlert && this._sentAppAlerts.has(alertType)) return;
		if (!isServiceAlert) this._sentAppAlerts.set(alertType, true);

		logger.logGeneral.log({}, `Sending alert of type ${alertType} with message: ${customMessage}`);

		const alertDate = new Date().toISOString();
		const passKey = await passKeyHelper.generatePassKey();

		const legacyBpkAlert = {
			kioskID: this.kioskId,
			customerID: this.companyId,
			alertDate,
			alertText: customMessage,
			alertType,
			passKey,
		};

		let sendAlert = true;
		if (import.meta.env.VITE_CONFIRM_ALERTS === 'true' && !isServiceAlert)
			sendAlert = confirm(`Send alert of type ${alertType} with message: ${customMessage}?`);

		if (sendAlert)
			await sqlite.createApiCall({
				id: ulid().toUpperCase(),
				timestamp: new Date(),
				requestData: JSON.stringify(legacyBpkAlert),
				type: ApiCallServiceType.Alert,
			});
	};

	legacyPostCashBoxRemoved = async (data: Omit<LecacyPostCashBoxRemoved, 'passKey' | 'customerID' | 'kioskID'>) => {
		const post = {
			...data,
			passKey: await passKeyHelper.generatePassKey(),
			customerID: this.companyId,
			kioskID: this.kioskId,
		};
		let posted;
		try {
			posted = await api.legacyBpkAdmin.postCashBoxRemoved(post);
		} catch (error) {
			logger.logGeneral.error({}, 'Error posting cash box removal to legacy BPK webservices:', error);
		} finally {
			if (posted) logger.logGeneral.log({}, 'Cash box removed posted to legacy BPK webservices', post);
			else logger.logGeneral.error({}, 'The legacy BPK webservices API rejected the cash box removal post', posted, post);
		}
	};

	legacyIsMaintenanceMode = async (): Promise<boolean> => {
		const post = {
			kioskID: this.kioskId,
			customerID: this.companyId,
			passKey: await passKeyHelper.generatePassKey(),
		};
		let isMaintenanceModeOn;
		try {
			isMaintenanceModeOn = await api.legacyBpkAdmin.getIsMaintenanceModeOn(post);
			return isMaintenanceModeOn;
		} catch (error) {
			logger.logGeneral.error({}, 'Error checking maintenance mode:', error);
			return false;
		} finally {
			logger.logGeneral.log({}, 'Kiosk maintenance mode status', isMaintenanceModeOn);
		}
	};

	legacyIsApiActive = async (): Promise<boolean> => {
		let isApiActive;
		try {
			isApiActive = await api.legacyBpkAdmin.getIsApiActive();
			return isApiActive;
		} catch (error) {
			logger.logGeneral.error({}, 'Error checking legacy API status:', error);
			return false;
		} finally {
			if (isApiActive) logger.debug.log('Legacy API is active', isApiActive);
			else logger.logGeneral.error({}, 'Legacy API is not active', isApiActive);
		}
	};

	/**
	 * Determines if a payment method can be used for a given amount.
	 * Checks both kiosk-level payment method configuration and each cart item's payment methods.
	 * If either the kiosk-level config or any cart item explicitly disallows the payment type, returns false.
	 * @param amountOfPayment - The amount to be paid.
	 * @param paymentType - The type of payment method to check. If not provided, the method will return canPay: true.
	 * @returns An object with canPay (boolean) and an optional limitReason ('over-limit' | 'under-limit').
	 */
	canPayAmountWithPaymentMethod = (amountOfPayment: number, paymentType?: PaymentType): PaymentMethodCheckResult => {
		if (paymentType === undefined) return { canPay: true };

		// --- Kiosk-level check ---
		const paymentMethods = store.configStore.kioskMethodsOfPayment;
		const kioskPaymentMethod = paymentMethods.find((method: PaymentMethod) => (method.paymentType & paymentType) === paymentType);

		if (!kioskPaymentMethod) {
			logger.logGeneral.log({}, `No kiosk payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) found`);
		} else if (!kioskPaymentMethod.isAllowed) {
			logger.logGeneral.log({}, `Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) is not allowed at kiosk level`);
			return { canPay: false };
		} else if (kioskPaymentMethod.minAmount != null && amountOfPayment < kioskPaymentMethod.minAmount) {
			logger.logGeneral.log(
				{},
				`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) does not allow amounts less than ${kioskPaymentMethod.minAmount} (kiosk level)`
			);
			return { canPay: false, limitReason: 'under-limit' };
		} else if (kioskPaymentMethod.maxAmount != null && amountOfPayment > kioskPaymentMethod.maxAmount) {
			logger.logGeneral.log(
				{},
				`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) does not allow amounts greater than ${kioskPaymentMethod.maxAmount} (kiosk level)`
			);
			return { canPay: false, limitReason: 'over-limit' };
		}

		// --- Cart-item-level check ---
		const cartItems = store.accountStore.cartItems;
		for (const cartItem of cartItems) {
			const itemPaymentMethod = cartItem.item.paymentMethods?.find((m: PaymentMethod) => (m.paymentType & paymentType) === paymentType);
			if (!itemPaymentMethod) continue;

			if (!itemPaymentMethod.isAllowed) {
				logger.logCIS.log(
					{},
					`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) is not allowed by cart item ${cartItem.id}`
				);
				return { canPay: false };
			}
			if (itemPaymentMethod.minAmount != null && cartItem.amountToPay < itemPaymentMethod.minAmount) {
				logger.logCIS.log(
					{},
					`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) does not allow amounts less than ${itemPaymentMethod.minAmount} (cart item ${cartItem.id})`
				);
				return { canPay: false, limitReason: 'under-limit' };
			}
			if (itemPaymentMethod.maxAmount != null && cartItem.amountToPay > itemPaymentMethod.maxAmount) {
				logger.logCIS.log(
					{},
					`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) does not allow amounts greater than ${itemPaymentMethod.maxAmount} (cart item ${cartItem.id})`
				);
				return { canPay: false, limitReason: 'over-limit' };
			}
			if (itemPaymentMethod.rollingLimitInfo != null && cartItem.amountToPay > itemPaymentMethod.rollingLimitInfo.remainingLimit) {
				logger.logCIS.log(
					{},
					`Payment method of type ${paymentType} (${this.paymentTypeName(paymentType)}) exceeds rolling limit for cart item ${cartItem.id} (remaining: ${itemPaymentMethod.rollingLimitInfo.remainingLimit}, requested: ${cartItem.amountToPay})`
				);
				return { canPay: false, limitReason: 'over-limit' };
			}
		}

		return { canPay: true };
	};

	getPaymentMethodLimits = (paymentType?: PaymentType): { min?: number; max?: number } => {
		if (paymentType === undefined) return {};

		const paymentMethods = store.configStore.kioskMethodsOfPayment;
		const kioskPaymentMethod = paymentMethods.find((method: PaymentMethod) => (method.paymentType & paymentType) === paymentType);

		let min = kioskPaymentMethod?.minAmount ?? undefined;
		let max = kioskPaymentMethod?.maxAmount ?? undefined;

		const cartItems = store.accountStore.cartItems;
		for (const cartItem of cartItems) {
			const itemPaymentMethod = cartItem.item.paymentMethods?.find((m: PaymentMethod) => (m.paymentType & paymentType) === paymentType);
			if (!itemPaymentMethod) continue;

			if (itemPaymentMethod.minAmount != null) min = min != null ? Math.max(min, itemPaymentMethod.minAmount) : itemPaymentMethod.minAmount;
			if (itemPaymentMethod.maxAmount != null) max = max != null ? Math.min(max, itemPaymentMethod.maxAmount) : itemPaymentMethod.maxAmount;
			if (itemPaymentMethod.rollingLimitInfo) {
				const remaining = itemPaymentMethod.rollingLimitInfo.remainingLimit;
				max = max != null ? Math.min(max, remaining) : remaining;
			}
		}

		return { min, max };
	};

	private paymentTypeName = (value: PaymentType): string => {
		if (value === 0) return PaymentType[0] ?? '0';
		const names: string[] = [];
		for (const key of Object.keys(PaymentType)) {
			const flag = Number(key);
			if (!isNaN(flag) && flag !== 0 && (value & flag) === flag) names.push(PaymentType[flag]);
		}
		return names.length ? names.join(' | ') : String(value);
	};

	get paymentReferenceId(): string {
		const sid = store.sessionStore.sessionId ?? '?';
		const lid = store.sessionStore.legacyId ?? '?';
		return `SID:${sid}|LID:${lid}`;
	}
}
